import { Alert, AlertTitle, Box, Typography } from '@mui/material'
import { SP, FS } from '../../theme/tokens'
import { v2Tone } from '../../theme/theme'

/**
 * LeistungsHinweis — welche Leistungen in den bestellten Leistungen einer
 * Sendung FEHLEN (der Stand, nicht die Folgerung daraus).
 *
 * @description
 *   Zeigt, wenn in `shippingInfo.orderedKnServices` weder `del` (Zustellung)
 *   noch `ich` (Importverzollung) steht — und zeigt **zusammen damit** die
 *   Codes, die in keiner Liste der K&N-Dokumentation vorkommen.
 *
 *   🔴 **Die unbekannten Codes sind der Kern, nicht die Fussnote.** Bei den
 *   gemessenen Sendungen sind sie die Mehrheit (`173`, `sec`, `cfe`, `cfh`),
 *   und K&Ns eigene Doku sagt, dass ihre Bedeutung offen ist — *„Frage an K&N,
 *   nicht Annahme"*. Ohne sie laese sich der Hinweis als „K&N liefert nicht
 *   bis zum Lager", und genau das ist **nicht** gemessen.
 *
 *   Deshalb zwei Stufen, und der Unterschied ist das Verb:
 *     * **Stufe A** — alle Codes bekannt: „… are not booked as a K&N service"
 *       (englisch seit O51; der Titel wird ZUSAMMENGESETZT, Verb nach Anzahl).
 *     * **Stufe B** — mindestens ein unbekannter Code: nur „The ordered
 *       services do not list …", plus die offene Frage.
 *
 *   `severity="warning"`, nie `error`: es ist ein Befund ueber eine Buchung,
 *   kein Defekt des Systems. Und er verschwindet ganz, sobald `del` und `ich`
 *   gebucht sind — ein Hinweis, der immer steht, wird nicht gelesen.
 *
 * @useWhen
 *   - Eine gespiegelte K&N-Sendung wird angezeigt (Liste oder Detail)
 *   - Es soll sichtbar sein, was der Spediteur NICHT uebernommen hat
 *
 * @dontUseFor
 *   - Fehlende Termine — das ist der Normalfall und braucht keinen Alarm,
 *     sondern einen Strich und die Legendenzeile
 *   - Fehler des Abzugs — die stehen in der Laufzeile, nicht an der Sendung
 *
 * @related
 *   - backend/kn/ansicht.py::leistungsbild — erzeugt genau diese fuenf Zustaende
 *
 * @reuseHints
 *   - S10 kann denselben Baustein im `SendungDialog` zeigen, sobald eine
 *     Eingangssendung mit dem Spiegel verknuepft ist
 *
 * @props
 *   bild — das `leistungsbild`-Objekt aus der Antwort. `null`/fehlend
 *          rendert nichts.
 *   knapp — true fuer die Listenansicht: nur die Kopfzeile, ohne Begruendung
 */
export default function LeistungsHinweis({ bild, knapp = false }) {
  if (!bild) return null

  // 🔴 K&N hat gar keine Liste geliefert. Dann gibt es nichts, worin etwas
  //    fehlen koennte -- eine Fehlt-Aussage waere hier erfunden.
  if (!bild.gemeldet) {
    if (knapp) return null
    return (
      <Alert severity="info" sx={{ mb: SP.card }}>
        K&amp;N reports no ordered services for this shipment.
      </Alert>
    )
  }

  if (bild.form_unerwartet) {
    if (knapp) return null
    return (
      <Alert severity="info" sx={{ mb: SP.card }}>
        K&amp;N returns the ordered services in an unexpected shape. The raw
        record of the shipment is stored.
      </Alert>
    )
  }

  if (bild.leer) {
    if (knapp) return null
    return (
      <Alert severity="info" sx={{ mb: SP.card }}>
        K&amp;N holds an empty service list for this shipment.
      </Alert>
    )
  }

  const fehlend = bild.fehlend || []
  const unbekannt = bild.unbekannt || []
  const bestellt = bild.bestellt || []

  // 🔴 Die Gegenprobe zu allem darunter: ist nichts zu melden, steht nichts da.
  if (fehlend.length === 0 && unbekannt.length === 0) return null

  // 🔴 Bejahend auf STUFE A pruefen, nicht verneinend auf B. Die Vorgabe
  //    zeigte vorher (`stufeB = stufe === 'B'`) auf den STAERKEREN Satz: jeder
  //    unerwartete Wert — `null`, `undefined`, ein kuenftiges `'C'` — haette
  //    „ist nicht gebucht" ergeben, also ausgerechnet die Aussage, deren
  //    Einschraenkung diese Flaeche traegt. Regel-15-Tor, W4.
  const stufeA = bild.stufe === 'A'
  // 🔴 ZWEI Dinge in einer Zeile, und beide sind im gemessenen NORMALFALL
  //    sichtbar: `BEOBACHTETE_LEISTUNGEN = ("DEL","ICH")`, und beide fehlen
  //    bei beiden gemessenen Codelisten (`test_ansicht.py:208`). Das
  //    Bindewort war deutsch geblieben, und `is` stand bei zwei Subjekten.
  //    Vom Regel-16-Tor GERENDERT, nicht erschlossen:
  //      „Delivery und Import customs clearance is not booked as a K&N service"
  const namen = fehlend.map((f) => f.bezeichnung).join(' and ')
  const istSind = fehlend.length === 1 ? 'is' : 'are'
  const codes = fehlend.map((f) => f.code.toLowerCase()).join(', ')

  const titel = fehlend.length === 0
    ? 'Unmatched service codes'
    : stufeA
      // Stufe A: alle Codes bekannt, der Schluss auf die Buchung ist gedeckt.
      ? `${namen} ${istSind} not booked as a K&N service`
      // Sonst: nur die Beobachtung an der Liste, kein Schluss auf die Buchung.
      // 🔴 F9 (UI-Durchgang 2, 2026-10-07): der Satz nannte NUR die Kuerzel
      //    („do not list del, ich") — im Foto stand er als „K&N-Codes ohne
      //    Erklaerung". Jetzt traegt er den Namen der Leistung, das Kuerzel
      //    steht in Klammern — dieselbe Form wie die „Missing:"-Zeile darunter
      //    („Delivery (del)") und wie der Stufe-A-Titel, der die Namen schon
      //    fuehrt. `bezeichnung` ist englisch (backend/kn/ansicht.py).
      : `The ordered services do not list ${namen} (${codes})`

  if (knapp) {
    // 🔴 `v2Tone(t,'warn').main` statt `warning.main`: der MUI-Standard liegt
    //    auf weiss bei 3,11:1, dieser Token bei 5,02:1 — er ist genau dafuer
    //    eine Stufe dunkler gestimmt. Es ist der EINZIGE Hinweis, den die
    //    Liste ueberhaupt zeigt (Regel-16-Tor, B4).
    return (
      <Typography component="span"
                  sx={{ fontSize: FS.sm, color: (t) => v2Tone(t, 'warn').main }}>
        {titel}
      </Typography>
    )
  }

  return (
    <Alert severity="warning" sx={{ mb: SP.card }}>
      <AlertTitle sx={{ fontSize: FS.lg }}>{titel}</AlertTitle>

      {fehlend.length > 0 && (
        <Typography sx={{ fontSize: FS.base, mb: SP.chip }}>
          Missing: {fehlend.map((f) => `${f.bezeichnung} (${f.code.toLowerCase()})`).join(', ')}
        </Typography>
      )}

      <Typography sx={{ fontSize: FS.base, mb: SP.chip }}>
        Ordered:{' '}
        {bestellt.length > 0
          ? bestellt.map((b) => `${b.name} (${b.code.toLowerCase()})`).join(', ')
          : 'no known service'}
      </Typography>

      {unbekannt.length > 0 && (
        <Box sx={{ mb: SP.chip }}>
          <Typography sx={{ fontSize: FS.base, fontWeight: 600 }}>
            Unmatched: {unbekannt.map((c) => c.toLowerCase()).join(', ')}
          </Typography>
          <Typography sx={{ fontSize: FS.md, color: 'text.secondary' }}>
            These codes appear in no list in the K&amp;N documentation. Whether
            they encode delivery or customs clearance is open — that is a
            question for K&amp;N, not an assumption.
          </Typography>
        </Box>
      )}

      <Typography sx={{ fontSize: FS.md, color: 'text.secondary' }}>
        Only the list of ordered services is measured. It says nothing about
        what K&amp;N does outside that list.
      </Typography>
    </Alert>
  )
}
